/**
 * Web-side entry point for the Expo ↔ Vue bridge. Importing this file once at
 * app boot installs `window.__zbReceive` (via the shared `web.ts` module) and
 * registers every method native can call.
 *
 *   import './native';
 *
 * To call native from anywhere in the SPA:
 *
 *   import { callNative } from '@/native';
 *   const { pong } = await callNative('ping', { msg: 'hi' });
 */

import { ref } from 'vue';
import {
    callNative,
    isInReactNativeWebView,
    registerWebHandler,
} from '@lib/deliveryteamCourierWebviewBridge';
import { authManager, authState, waitUntilReady } from '@api-client/auth/auth';
import { ZMG_AUTH_STATE_CHANGED_EVENT, type AuthState } from '@api-client/auth/auth.class';
import '@deliveryteam-spa-orders-vue/refs/pushNotification';
import { setAuthState } from "@api-client/auth/auth";

/** Last status string pushed by native — bind in components if useful. */
export const nativeStatus = ref<string>('');

// registerWebHandler('requestReload', () => {
//     window.location.reload();
// });

/**
 * Native pushed refreshed tokens (it ran a bg refresh while the webview was
 * suspended). Hydrate locally AND broadcast so other tabs/iframes catch up.
 */
registerWebHandler('setTokensFromNative', async (state) => {
    await authManager.setAuthState(state as AuthState, /* broadcast */ true);
});

/**
 * Forward every SPA-side auth change to native so SecureStore stays in sync.
 * Only the courier RN webview SPA does this. The event is a same-document
 * CustomEvent — tokens never leave the page boundary.
 */
if (isInReactNativeWebView()) {
    const pushTokensToNative = (state: AuthState) => {
        void callNative('setTokens', state).catch((e) => {
            if (import.meta.env.DEV) console.warn('[native] setTokens failed:', e);
        });
    };
    document.addEventListener(ZMG_AUTH_STATE_CHANGED_EVENT, (e) => {
        const detail = (e as CustomEvent<AuthState>).detail;
        if (detail) pushTokensToNative(detail);
    });
    // Bootstrap: send the loaded state once after IDB hydration.
    void waitUntilReady.then(async () => {
        await restoreTokensFromExpo(); // wait until restoring checks
        pushTokensToNative(authState.value);
    });
}

async function restoreTokensFromExpo() {
    if (authState.value.accessTokenPayload === null) {
        // try restoring from expo tokens, they maybe still valid
        // sometimes android or ios removes webview data to save storage.

        // wait 3 seconds
        // await new Promise(resolve => setTimeout(resolve, 5000));
        const authTokensSnapshot = await callNative('getTokens', undefined);
        if (
            // access token maybe expired no need to check it, 
            // I have refresh token here and it is enough to proceed
            // authTokensSnapshot.accessToken !== null &&
            // authTokensSnapshot.accessTokenPayload !== null &&
            // authTokensSnapshot.accessTokenPayload.expiresAt > Date.now() &&
            authTokensSnapshot.refreshToken !== null &&
            authTokensSnapshot.refreshTokenPayload !== null &&
            authTokensSnapshot.refreshTokenPayload.expiresAt > Date.now()
        ) {
            await setAuthState({
                accessToken: authTokensSnapshot.accessToken,
                accessTokenPayload: authTokensSnapshot.accessTokenPayload,
                refreshToken: authTokensSnapshot.refreshToken,
                refreshTokenPayload: authTokensSnapshot.refreshTokenPayload,
            } as AuthState, true);
        } else {
            await callNative('setTokens', {
                accessToken: null,
                accessTokenPayload: null,
                refreshToken: null,
                refreshTokenPayload: null,
            });
            await callNative('stopLocationTracking', undefined)
        }
    }
}

export { callNative, isInReactNativeWebView };
